import { customElement, FASTElement, observable } from '@microsoft/fast-element';
import { RFQSelectorTemplate as template } from './rfq-selector.template';
import { RFQSelectorStyles as styles } from './rfq-selector.styles';
import { IRfq, IVppa } from '../../../../types';
import { IRfqService, IVppaService } from '../../../../services';
import { IStructuredChatService } from '../../../../services/structured-chat.service';
import { ZeroGridPro } from '@genesislcap/foundation-zero-grid-pro';
import type { StructuredChatEnums } from '../structured-chat';
import { PRODUCT_TYPE, TECHNOLOGY } from '../../../../utils';

const name = 'rfq-selector';

@customElement({
  name,
  template,
  styles,
})
export class RFQSelector extends FASTElement {
  @IRfqService rfqService: IRfqService;
  @IVppaService vppaService: IVppaService;
  @IStructuredChatService structuredChatService: IStructuredChatService;
  // These need to be refactored once RFQ dropdown is removed and actual functionality is added
  @observable allRFQs: IRfq[] = [];
  @observable rfqGrid: ZeroGridPro;
  @observable disabledLinkBtn: boolean = true;
  @observable selectedVppa: IVppa = {} as IVppa;
  @observable isSummaryOpen: boolean = false;
  @observable summaryData = {};
  @observable rfqCompanyName: string;

  @observable structuredChatEnums: StructuredChatEnums = {
    applicableRECProgram: [],
    applicableTrackingSystem: [],
    recDeliveryMethodology: [],
    alternativeSwapSettlementCalculationMethod: [],
    technologyTypes: [],
    performanceGuaranteeMethod: [],
    negativePricingMethod: [],
    negotiationStatus: [],
    contractTypes: [],
  };

  connectedCallback() {
    super.connectedCallback();
    this.setGridOptions();
    this.getVPPAInfo();
  }

  public toggleSummary() {
    this.isSummaryOpen = !this.isSummaryOpen;
  }

  public setGridOptions() {
    this.rfqGrid.gridOptions = {
      onGridReady: (grid) => {
        this.getRFQs();
        grid.api.addEventListener('firstDataRendered', (params) => {
          params.api.forEachNode((node) => node.setSelected(node.data.IS_LINKED));
        });

        grid.api.addEventListener('rowSelected', () => {
          this.checkSelected();
        });
      },
      rowClass: 'transparent-background',
      suppressBrowserResizeObserver: true,
      defaultColDef: {
        resizable: true,
        wrapHeaderText: true,
        autoHeaderHeight: true,
      },
    };
  }

  // Get the RFQ data to populate the RFQ dropdown
  // TODO: had to be changed so that this data is utilised in the RFQ selection page
  public async getRFQs() {
    if (
      this.structuredChatService.selectedVPPAIDs.length &&
      !this.structuredChatService.selectedRFQIDs.length
    ) {
      this.getVPPAInfo();
      this.rfqRequest();
    } else if (
      this.structuredChatService.selectedVPPAIDs.length &&
      this.structuredChatService.selectedRFQIDs.length
    ) {
      await this.rfqRequest();
      setTimeout(() => {
        this.rfqGrid.gridApi.forEachNode((row) => {
          const rfq = this.structuredChatService.selectedRFQIDs.filter(
            (rfqID: string) => rfqID === row.data.RFQ.RFQ_ID
          );
          if (rfq.length > 0 || row.data.IS_LINKED) {
            row.setSelected(true);
          }
        });
      }, 100);
    }
  }

  public async rfqRequest() {
    await this.rfqService
      .getRfqVppaNegotiation(this.structuredChatService.selectedVPPAIDs[0])
      .then((response) => {
        let rfqs = response.REPLY;
        if (response && response.REPLY.length) {
          rfqs = response.REPLY.filter(
            (rfq) =>
              (rfq.RFQ.TECHNOLOGY === TECHNOLOGY.BOTH || this.selectedVppa?.TECHNOLOGY == rfq.RFQ.TECHNOLOGY) &&
              this.selectedVppa?.ISO === rfq.RFQ.ISO &&
              this.selectedVppa?.PRODUCT_TYPE === rfq.RFQ.PRODUCT_TYPE &&
              this.selectedVppa?.CONTRACT_TYPE === rfq.RFQ.CONTRACT_TYPE &&
              (this.selectedVppa?.PRODUCT_TYPE === PRODUCT_TYPE.RECS || this.selectedVppa?.SETTLEMENT_POINT === rfq.RFQ.SETTLEMENT_POINT) &&
              (this.selectedVppa?.APPLICABLE_REC_PROGRAM === rfq.RFQ.RFQ_APPLICABLE_REC_PROGRAM || rfq.RFQ.RFQ_APPLICABLE_REC_PROGRAM === 'ANY')
          );
        }
        this.compare(rfqs);
        this.rfqGrid.gridApi.setRowData(rfqs);
      });
  }

  public getVPPAInfo() {
    if (this.structuredChatService.selectedVPPAIDs.length > 0) {
      this.vppaService.getVppa(this.structuredChatService.selectedVPPAIDs[0]).then((response) => {
        if (response) {
          this.selectedVppa = response.REPLY[0];
        }
      });
    }
  }

  public linkRFQs() {
    const selectedRFQs = this.rfqGrid.gridApi.getSelectedRows();
    this.structuredChatService.selectedRFQIDs = selectedRFQs
      .filter((rfq) => {
        if (!rfq.IS_LINKED) return rfq;
      })
      .map((rfq) => rfq.RFQ.RFQ_ID);
    this.$emit('closeRFQSelector', true);
  }

  public cancelRFQLinking() {
    if (this.structuredChatService.selectedRFQIDs.length) {
      this.$emit('closeRFQSelector', true);
    } else {
      this.structuredChatService.selectedVPPAIDs = [];
      this.structuredChatService.negotiationInView = this.structuredChatService.allNegotiations[0];
      this.$emit('closeRFQSelector', true);
    }
  }

  public checkSelected() {
    if (!this.rfqGrid.gridApi) {
      return;
    }

    let selectedRFQs = this.rfqGrid.gridApi.getSelectedRows();
    selectedRFQs = selectedRFQs.filter((rfq) => !rfq.IS_LINKED);
    this.disabledLinkBtn = !selectedRFQs.length;
  }
    
private compare(rfqs:any[]) {
  return rfqs.sort((a, b) => {
    if (a.RFQ.CREATED_AT > b.RFQ.CREATED_AT) {
      return -1;
    } else if (a.RFQ.CREATED_AT < b.RFQ.CREATED_AT) {
      return 1; 
    } else {
      return 0;
    }
  });
}
}